import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  TpvHeader,
  TpvSection,
  TpvWrapper,
  TpvHeaderTitle,
  PepperTpvContainer,
} from './VerifyCardTpvStyledComponents';
import { PepperTpv } from "@pepper/tpv-react";
import { useEffect, useState } from "react";
import { ContainerBackgroundMenu } from "../../components/CustomerHeader/CustomerHeaderStyledComponents";
import { TextBold, TextRegular } from "../../styles/GlobalStyledComponents";
import { Transition } from "../Transition";
import { colors } from '../../styles/Colors';
import { ROUTES_PATH } from "../../constants";
import TokenService from "../../services/Token";
import FinancialService from "../../services/Financial";
import { PaymentMethod } from "../../interfaces/PaymentMethod";
import { LoanResponse, PaymentCard, PaymentDataTypeEnum } from "../../interfaces/Loan";
import { TransactionReasonEnum } from "../../interfaces/Debt";
import { UserResponse } from "../../interfaces/UserData";
import IconCardPlus from "../../assets/icons/IconCardPlus";
import { saveUrlParams } from "../../utils/urlParams";
import { convertLeadToken } from "../../utils/convertLeadToken";
import { initTracking } from "@pepper/utm-react";

const VerifyCardTpv = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const leadToken = searchParams.get('lead_token');
  const viewMode = searchParams.get('viewMode');

  const search = useLocation().search;
  const urlParams = new URLSearchParams(search);

  const [documentId, setDocumentId] = useState<string | null>(null);
  const [loading, setloading] = useState(true);
  const [paymentId, setPaymentId] = useState('');
  const [tpvToken, setTPVToken] = useState('');
  const [userData, setUserData] = useState<UserResponse | null>(null);
  const [expiredLastDigits, setExpiredLastDigits] = useState('');
  const [expiredLoandId, setExpiredLoandId] = useState('');
  const { red4, white } = colors;

  const navigate = useNavigate();
  const environmentType: any = {
    type: process.env.REACT_APP_ENV,
  };
  const isExpired = (expirationDate: string) => {
    const [month, year] = expirationDate.split('/');
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    return (2000 + parseInt(year) < currentYear) ||
      (2000 + parseInt(year) === currentYear && parseInt(month) - 1 <= currentMonth);
  };

  const getExpiredLoans = (): Promise<LoanResponse[]> => {
    return new Promise((resolve, reject) => {
      FinancialService.getLoans().then((data) => {
        const expiredLoans = data.loans.filter((loan) => {
          if (loan.payment_data.type === PaymentDataTypeEnum.Card) {
            return isExpired(loan.payment_data.expiration_date)
          }
          return false;
        }
        );
        if (expiredLoans.length > 0) {
          resolve(expiredLoans);
        } else {
          resolve([]);
        }
      }).catch((error) => {
        reject(error);
      });
    });
  }

  const createPayment = (documentId: string) => {
    return new Promise((resolve, reject) => {
      FinancialService.createPayment({
        document_id: documentId,
        loan_id: expiredLoandId,
        payment_method: PaymentMethod.CREDIT_CARD,
        transaction_type: 'VERIFY',
        transaction_reason: TransactionReasonEnum.ALT
      }).then((data) => {
        setTPVToken(TokenService.getToken() || '');
        setPaymentId(data.payment_id);
        resolve(data);
      }).catch(() => {
        handleError()
      });
    });
  }

  const fetchInitialData = async () => {
    try {
      const userData = await FinancialService.getUserData();
      sessionStorage.setItem("verifyCardName", userData.name);
      setDocumentId(userData.document_id);
      const expiredLoans = await getExpiredLoans();
      setUserData(userData);
      if (expiredLoans.length > 0) {
        setExpiredLoandId(expiredLoans[0].loan_id);
        setExpiredLastDigits(expiredLoans[0].payment_data.last_four_digits);
      } else {
        navigate(ROUTES_PATH.CARD_UP_TO_DATE, {
          state: {
            userName: userData?.name
          }
        });
      }
      await createPayment(userData.document_id);
      setloading(false);
    } catch {
      handleError()
    }
  }

  const checkRecentPaymentMethods = (attempt: number = 1): Promise<PaymentCard | null> => {
    if (!documentId) {
      return Promise.resolve(null);
    }
    return new Promise((resolve, reject) => {
      FinancialService.getPaymentCards(documentId).then((data) => {
        const now = new Date();
        const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60000);

        const recentPaymentMethods = data.payment_methods.filter((method) => {
          const consentDate = new Date(method.consent_last_modification);
          return consentDate >= fifteenMinutesAgo && consentDate <= now;
        });

        if (recentPaymentMethods.length > 0) {
          const sortedCards = recentPaymentMethods.sort((a, b) => {
            const dateA = new Date(a.consent_last_modification);
            const dateB = new Date(b.consent_last_modification);
            return dateB.getTime() - dateA.getTime();
          });

          resolve(sortedCards[0]);
        } else if (attempt < 4) {
          setTimeout(() => {
            checkRecentPaymentMethods(attempt + 1)
              .then(resolve)
              .catch(() => handleError());
          }, 1500);
        } else {
          // console.log('Max attempts reached.');
          handleError()
        }
      }).catch(() => {
        reject(() => handleError());
      });
    });
  };

  const handleSuccessEnd = () => {
    checkRecentPaymentMethods()
      .then((recentPaymentMethod) => {
        if (recentPaymentMethod) {
          FinancialService.patchPaymentData(expiredLoandId, recentPaymentMethod.payment_method_id).then((data) => {
            navigate(ROUTES_PATH.SUCCESS, {
              state: {
                isVerifyCard: true,
              }
            });
          }).catch(() => {
            handleError()
          });
        }
      })
      .catch(() => {
        handleError()
      });
  };

  const handleError = () => {
    navigate(ROUTES_PATH.UNKNOWN_ERROR, {
      state: {
        isVerifyCard: true,
      }
    });
  }

  useEffect(() => {
    sessionStorage.setItem("isVerifyCard", "true");
    if (viewMode) {
      const value = viewMode.toUpperCase();
      sessionStorage.setItem("pepper-view-mode", value);
    }
    if (token) {
      TokenService.setToken(token);
    }
    if (!documentId) {
      fetchInitialData();
    }
  }, [token, documentId]);

  useEffect(() => {
    if (urlParams.size > 0 && process.env.REACT_APP_ENV && leadToken) {
      const trackedData = saveUrlParams(urlParams);
      if (trackedData) {
        const decodedToken = leadToken && convertLeadToken(leadToken);
        if (decodedToken){
          initTracking(
            process.env.REACT_APP_ENV,
            decodedToken,
            'lead_token',
            'Landing_Pagos'
          );
        }
      }
    }
  }, [])

  return (
    <>
      {!loading && (
        <TpvSection id="tpv-page">
          <TpvWrapper id="tpv-wrapper">
            <ContainerBackgroundMenu id="container-background-menu">
              <TpvHeader id="tpv-header">
                <IconCardPlus />
                <TpvHeaderTitle>
                  <TextBold fontSize={16} lineHeight={22} fontWeight={800}>
                    Hola {userData?.name} 😃
                  </TextBold>
                  <TextRegular
                    fontSize={14}
                    lineHeight={20}
                    color="gray6"
                    id="tpv-card-title"
                  >
                    <TextBold fontSize={14} fontWeight={700}>
                      ¡Actualiza tu tarjeta!{" "}
                    </TextBold>
                    La tarjeta terminada en **** {expiredLastDigits} está próxima a caducar.
                  </TextRegular>
                </TpvHeaderTitle>
              </TpvHeader>
            </ContainerBackgroundMenu>
            {paymentId && tpvToken && (
              <PepperTpvContainer id="pepper-tpv-container">
                <PepperTpv
                  token={tpvToken}
                  paymentId={paymentId}
                  successEnd={handleSuccessEnd}
                  error={handleError}
                  currentEnvironment={environmentType}
                  subtitle={''}
                  subtitlePosition="top"
                  generalPadding="15px 20px 40px 20px"
                  saveRedirectUrls={(e) => { }}
                  isIframe
                  isRedirection={false}
                  showInWidget={true}
                  buttonBackgroundColor={red4}
                  buttonTextColor={white}
                />
              </PepperTpvContainer>
            )}
          </TpvWrapper>
        </TpvSection>
      )}
      {loading && <Transition />}
    </>
  );;
};

export default VerifyCardTpv;
